import React from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { LocalOffer, Store as StoreIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients } from "../../utils/theme";

interface MallHubCTAProps {
  totalOffers: number;
  totalStores: number;
  totalEvents: number;
  onViewOffers: () => void;
  onViewStores: () => void;
  onViewEvents: () => void;
}

/**
 * MallHubCTA Component
 *
 * Call-to-action banner at the bottom of the page
 */
const MallHubCTA: React.FC<MallHubCTAProps> = ({
  totalOffers,
  totalStores,
  totalEvents,
  onViewOffers,
  onViewStores,
  onViewEvents,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.7 }}
    >
      <Card
        elevation={4}
        sx={{
          background: gradients.navyLux,
          color: "white",
          mb: 4,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <CardContent
          sx={{
            p: { xs: 3, md: 4 },
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                mb: 1,
                fontSize: { xs: "1.75rem", md: "2.125rem" },
              }}
            >
              Start Exploring Today
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.95,
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Everything you need in one place
            </Typography>
          </Box>

          {/* Action Cards Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: totalEvents > 0 ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
              },
              gap: 2,
              maxWidth: 900,
              mx: "auto",
            }}
          >
            {/* Offers Card */}
            <Box
              onClick={onViewOffers}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.2)",
                transition: "all 0.3s ease",
                textAlign: "center",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.25)",
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LocalOffer sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem" } }}
              >
                {totalOffers}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Exclusive Offers
              </Typography>
            </Box>

            {/* Stores Card */}
            <Box
              onClick={onViewStores}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.2)",
                transition: "all 0.3s ease",
                textAlign: "center",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.25)",
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem" } }}
              >
                {totalStores}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Amazing Stores
              </Typography>
            </Box>

            {/* Events Card - Only show if there are events */}
            {totalEvents > 0 && (
              <Box
                onClick={onViewEvents}
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                  p: { xs: 2, sm: 2.5 },
                  cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.2)",
                  transition: "all 0.3s ease",
                  textAlign: "center",
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.25)",
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 1, sm: 1.5 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      borderRadius: "50%",
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: 20, sm: 24 },
                    }}
                  >
                    🎉
                  </Box>
                </Box>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{ mb: 0.5, fontSize: { xs: "1.5rem", sm: "2rem" } }}
                >
                  {totalEvents}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  Upcoming Events
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MallHubCTA;
